import type { ComponentType, ReactNode } from "react";
import { Platform } from "react-native";
import type { RouterInputs, RouterOutputs } from "@utils/trpc";
import type { EngineCheckoutSession } from "@components/Checkout/engineSession";
import type { PurchaseBuyer } from "@components/Checkout/PurchaseSheet";
import type { CheckoutChrome } from "@components/Checkout/native/sheetButton";
import type { CheckoutGoods } from "@libs/checkout-core";
import { musicUnlockRefusal as sharedMusicUnlockRefusal } from "@libs/shared-ui";

/**
 * A music unlock in the app (LIS-1038): the purchase flow every sale runs
 * (PurchaseSheet) for one item, on the same engine checkout as a ticket,
 * for the signed-in fan. `accesses.createMusicUnlock` either unlocks the
 * music outright (free, or a valid unlock code: the server completes the
 * order), says the fan owns it already, or holds an engine checkout for
 * its price; `accesses.confirmMusicUnlock` grants it from the engine's
 * order as soon as that checkout says it is paid (the engine's webhook is
 * the backstop).
 */

export type MusicUnlockInput = RouterInputs["accesses"]["createMusicUnlock"];
export type MusicUnlockResult = RouterOutputs["accesses"]["createMusicUnlock"];
export type MusicOwnership =
  RouterOutputs["accesses"]["confirmMusicUnlock"]["status"];

/** The engine checkout the unlock's sheet draws (SheetCheckout). */
export type MusicCheckoutProps = {
  session: EngineCheckoutSession;
  /** Left before paying, or the hold ran out (with a message). */
  onBack: (message?: string) => void;
  /** Done with the checkout. */
  onClose: () => void;
  onChrome?: (chrome: CheckoutChrome) => void;
  /** The order is the fan's. */
  onSucceeded?: () => void;
  backLabel?: string;
  /** What the cart sells: an item, for its words before the session. */
  goods?: CheckoutGoods;
};

/** Everything a music unlock reaches beyond its own screen, so the flow
 *  runs against fakes as it does against the app (useMusicUnlockPorts). */
export type MusicUnlockPorts = {
  /** Every unlock is the fan's own, free or paid: they sign in, in the
   *  sheet's contact step, before anything is asked of the server. */
  buyer: PurchaseBuyer;
  /** Async results still belong to the account these ports were read
   *  for. */
  isCurrentUser(): boolean;
  create(input: MusicUnlockInput): Promise<MusicUnlockResult>;
  /** A hold returned after the sheet has already closed. */
  release(input: { accessId: string; clientSecret: string }): Promise<unknown>;
  confirm(input: {
    accessId: string;
    clientSecret: string;
  }): Promise<{ status: MusicOwnership }>;
  /** Details shows the music unlocked at once. */
  markUnlocked(accessId: string): void;
  /** The landing's unlock code has been used. */
  clearUnlockCode(): void;
  /** Details takes over, as after any purchase (`paymentResult`). */
  complete(accessId: string): void;
  /** The fan's accesses, read again. */
  refresh(): void;
  /** The unlock's sheet over Details, drawing `content` with the ports as
   *  they are in it: the fan may sign in there. */
  openSheet(
    content: (sheet: {
      close: () => void;
      ports: MusicUnlockPorts;
    }) => ReactNode,
  ): void;
  Checkout: ComponentType<MusicCheckoutProps>;
};

export const musicPurchasePlatform =
  (): MusicUnlockInput["purchasePlatform"] =>
    Platform.OS === "ios"
      ? "ios"
      : Platform.OS === "android"
        ? "android"
        : "app-web";

/** The held checkout, as the sheet's checkout runs on it. */
export const musicCheckoutSession = (
  accessId: string,
  held: Extract<MusicUnlockResult, { kind: "engine" }>,
  requestedAt: number,
): EngineCheckoutSession => ({
  accessId,
  url: held.url,
  clientSecret: held.clientSecret,
  apiBaseUrl: held.apiBaseUrl,
  timing: { requestedAt, sessionAt: Date.now() },
});

/** Why the checkout could not be held, in the fan's words, and whether
 *  trying again could help: a refusal's own words, or "try again". */
export function musicUnlockRefusal(error: unknown): {
  message: string;
  retry: boolean;
} {
  return (
    sharedMusicUnlockRefusal(error) ?? {
      message: "Could not open checkout. Please try again.",
      retry: true,
    }
  );
}

/** The hold ran out before paying (the web checkout's words for an item). */
export const MUSIC_UNLOCK_EXPIRED = "Your cart expired. Nothing was charged.";

/** What the checkout's back is called: back to the music, before it. */
export const BACK_TO_MUSIC = "Back";
